/**
 * Google Analytics 4 event helpers for ad-platform conversion tracking.
 *
 * The loader in index.html only attaches `window.gtag` when
 * VITE_GA_MEASUREMENT_ID is set at build time. Every helper here is a
 * no-op when gtag isn't present, so callers don't need to guard.
 *
 * Purchase events are deduped by Stripe checkout session_id via
 * localStorage, since the success URL is bookmarkable and reloadable.
 */

const PURCHASE_DEDUPE_PREFIX = "gtag_purchase_";

// Hardcoded tier → monthly USD value for Google Ads conversion bidding.
// Kept in sync with PLAN_INFO in pages/checkout/index.tsx by convention.
// This is intentionally not imported from a shared module: it's the
// conversion *value* signal sent to ad platforms, not the displayed
// price, and the two are allowed to diverge (e.g. annual averaging).
const TIER_VALUE_USD: Record<string, number> = {
  starter: 29,
  pro: 99,
  enterprise: 499,
  enterprise_plus: 999,
};

function gtagAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

export function trackSignUp(method = "oauth"): void {
  if (!gtagAvailable()) return;
  try {
    window.gtag!("event", "sign_up", { method });
  } catch {
    // Analytics must never break the app
  }
}

export interface TrackPurchaseOptions {
  sessionId: string;
  tier?: string | null;
  cycle?: "monthly" | "annual" | null;
}

export function trackPurchase({ sessionId, tier, cycle }: TrackPurchaseOptions): boolean {
  if (!sessionId) return false;
  if (!gtagAvailable()) return false;

  const dedupeKey = PURCHASE_DEDUPE_PREFIX + sessionId;
  try {
    if (localStorage.getItem(dedupeKey)) return false;
  } catch {
    // localStorage may be unavailable in private modes — fall through
    // and fire anyway. Better to over-report than to silently drop.
  }

  const value = tier ? TIER_VALUE_USD[tier] ?? 0 : 0;
  const annualMultiplier = cycle === "annual" ? 12 : 1;

  try {
    window.gtag!("event", "purchase", {
      transaction_id: sessionId,
      value: value * annualMultiplier,
      currency: "USD",
      items: tier
        ? [{ item_id: tier, item_name: tier, price: value, quantity: annualMultiplier }]
        : [],
    });
    try {
      localStorage.setItem(dedupeKey, "1");
    } catch {
      // ignore storage failures
    }
    return true;
  } catch {
    return false;
  }
}
